import { useState, useRef, useEffect } from "react";
import { Mic, StopCircle, Play, Loader } from "lucide-react";

// Audio Preview Button (TTS)
export function AudioPreviewButton({ text }) {
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const audioRef = useRef(null);

  const playTTS = async () => {
    if (!text || text.trim().length === 0) return;
    
    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: text.substring(0, 200) })
      });
      
      if (!response.ok) throw new Error("TTS failed");
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      
      if (audioRef.current) {
        audioRef.current.src = url;
        audioRef.current.play();
        setPlaying(true);
        audioRef.current.onended = () => {
          setPlaying(false);
          URL.revokeObjectURL(url);
        };
      }
    } catch (err) {
      console.error("TTS error:", err);
      alert("Erreur TTS - verifiez que le service est disponible");
    }
    setLoading(false);
  };

  return (
    <div className="mt-2">
      <audio ref={audioRef} className="hidden" />
      <button
        onClick={playTTS}
        disabled={loading || !text}
        className="w-full py-2 bg-violet-500 hover:bg-violet-600 disabled:bg-slate-300 text-white rounded-lg font-bold text-sm flex items-center justify-center gap-2"
      >
        {loading ? (
          <Loader size={16} className="animate-spin" />
        ) : playing ? (
          <StopCircle size={16} />
        ) : (
          <Play size={16} />
        )}
        {loading ? "Chargement..." : playing ? "Lecture..." : "Ecouter"}
      </button>
    </div>
  );
}

// Audio Recorder Component
export function AudioRecorder({ audioData, onRecorded }) {
  const [recording, setRecording] = useState(false);
  const [hasRecording, setHasRecording] = useState(!!audioData);
  const [error, setError] = useState(null);
  const [permissionStatus, setPermissionStatus] = useState("prompt");
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);

  useEffect(() => {
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: "microphone" }).then((result) => {
        setPermissionStatus(result.state);
        result.onchange = () => setPermissionStatus(result.state);
      }).catch(() => {
        setPermissionStatus("prompt");
      });
    }
  }, []);

  const startRecording = async () => {
    setError(null);
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("Votre navigateur ne supporte pas l'enregistrement audio");
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setPermissionStatus("granted");
      
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        const reader = new FileReader();
        reader.onloadend = () => {
          onRecorded(reader.result);
          setHasRecording(true);
        };
        reader.readAsDataURL(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setRecording(true);
    } catch (err) {
      console.error("Microphone error:", err);
      setPermissionStatus("denied");
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        setError("Permission microphone refusee. Autorisez l'acces dans les parametres du navigateur.");
      } else if (err.name === "NotFoundError") {
        setError("Aucun microphone detecte.");
      } else {
        setError(err.message || "Impossible d'acceder au microphone");
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const playRecording = () => {
    if (audioData && audioRef.current) {
      audioRef.current.src = audioData;
      audioRef.current.play();
    }
  };

  return (
    <div className="space-y-2">
      <audio ref={audioRef} className="hidden" />
      
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
          {error}
        </div>
      )}
      
      {permissionStatus === "denied" && !error && (
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-xl text-sm text-yellow-700">
          Microphone bloque. Cliquez sur l'icone cadenas dans la barre d'adresse pour autoriser.
        </div>
      )}
      
      {!recording ? (
        <button
          onClick={startRecording}
          className="w-full py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl font-bold flex items-center justify-center gap-2 transition-colors"
        >
          <Mic size={18} /> Enregistrer
        </button>
      ) : (
        <button
          onClick={stopRecording}
          className="w-full py-3 bg-slate-800 text-white rounded-xl font-bold flex items-center justify-center gap-2 animate-pulse"
        >
          <StopCircle size={18} /> Arreter ({Math.floor((Date.now() / 1000) % 60)}s)
        </button>
      )}
      
      {hasRecording && !recording && (
        <button
          onClick={playRecording}
          className="w-full py-2 bg-violet-100 text-violet-700 rounded-lg font-bold text-sm flex items-center justify-center gap-2"
        >
          <Play size={16} /> Ecouter l'enregistrement
        </button>
      )}
    </div>
  );
}
